<template>
    <div>
        <v-expansion-panels
            :key="taskComponentKey"
            v-model="openedPanel"
            accordion
        >
            <v-expansion-panel v-for="(task, taskIndex) in calculationImpositionTasks" :key="task.id" @click="handleImpositionTask(task, taskIndex)">
                <span v-if="task.isShared" class="v-expansion-panel__label" />
                <v-expansion-panel-header>
                    <v-row no-gutters align="center">
                        <v-col cols="auto" md="8" class="d-flex align-center p-0">
                            <v-col v-if="isCalculationEditable" cols="auto" class="mr-2 pt-0 pb-0">
                                <v-checkbox
                                    :input-value="task.isSelected"
                                    class="mt-0 mb-1"
                                    hide-details
                                    :disabled="isTaskDisabled(task)"
                                    @change="changeTaskSelection(task, $event)"
                                    @click.native="$event.stopPropagation()"
                                />
                            </v-col>
                            <v-col cols="auto" :class="{'error--text': !task.validated}">
                                {{ task.process.name }}
                                <div v-if="task.isShared" class="text-caption text--disabled">
                                    (łączone)
                                </div>
                            </v-col>
                        </v-col>
                        <v-col
                            v-if="task.gangCreationAllowed"
                            md="4"
                            class="text-end"
                            cols="auto"
                        >
                            <v-tooltip
                                v-for="(gang, id) in task.gangsOut"
                                :key="id"
                                :open-delay="tooltipDelay"
                                top
                            >
                                <template #activator="{on}">
                                    <v-btn 
                                        class="mr-4"
                                        fab
                                        dark
                                        small
                                        color="pink"
                                        v-on="on"
                                    >
                                        <strong>G{{ gang.runningNumber }}</strong>
                                    </v-btn>
                                </template>
                                <span>{{ gang.name }}</span>
                            </v-tooltip>
                        </v-col>
                    </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <ImpositionTask
                        v-if="isExpanded"
                        ref="panelContent"
                        :task-data="currentTask"
                        :task-index="currentTaskIndex"
                        :is-calculation-editable="isCalculationEditable"
                        :machines="machines"
                        :papers="papers"
                        :imposition-data="imposition"
                        :elastic-materials="elasticMaterials"
                        :color-templates="colorTemplates"
                        :current-task-index="currentTaskIndex"
                        :initial-task-object="initialTaskObject"
                        :initial-imposition-object="initialImpositionObject"
                        @update-task-on-panel-close="updateTaskOnPanelClose"
                        @update-task-on-panel-change="updateTaskOnPanelChange"
                    />
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
import { isEqual } from 'lodash'
import ImpositionTask from './ImpositionTask'

import { appConfig } from '@/config'

import { addSentryBreadcrumb } from '@/utils/errorHandler'

import parse from '@/mixins/parse'
import tooltipDelay from '@/mixins/tooltip'

import { mask } from 'vue-the-mask'
import priceFormatter from '@/mixins/priceFormatter'

export default {
    name: 'CalculationImpositionTasks',
    directives: { mask },
    components: { ImpositionTask },
    mixins: [ parse, tooltipDelay, priceFormatter ],
    props: {
        isCalculationEditable: {
            type: Boolean
        },
        impositionData: {
            type: Object,
            default: null
        },
        machines: {
            type: Array,
            default: null
        },
        papers: {
            type: Array,
            default: null
        },
        colorTemplates: {
            type: Array,
            default: null
        },
        elasticMaterials: {
            type: Array,
            default: () => []
        },
        calculation: {
            type: Object,
            default: () => {}
        },
    },
    data() {
        return {
            taskComponentKey: 0, //
            openedPanel: null,
            imposition: this.impositionData,
            currentTask: {
                isExpanded: false
            },
            initialTaskObject: {},
            initialImpositionObject: {},
            formatsHeaders: [
                {
                    text: 'Nazwa komponentu',
                    value: 'componentName',
                    align: 'left',
                    sortable: false,
                    width: 200,
                    showOnReadOnly: true
                },
                {
                    text: 'Format A wyjścia',
                    value: 'outputFormatA',
                    align: 'center',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Format B wyjścia',
                    value: 'outputFormatB',
                    align: 'center',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 80,
                    showOnReadOnly: false
                },
            ],
            packagesHeaders: [
                {
                    text: 'Produkt',
                    value: 'productName',
                    align: 'left',
                    sortable: false,
                    showOnReadOnly: true
                },
                {
                    text: 'Nakład',
                    value: 'productAmount',
                    align: 'center',
                    sortable: false,
                    width: 100,
                    showOnReadOnly: true
                },
                {
                    text: 'Sposób pakowania',
                    value: 'boxType',
                    align: 'center',
                    sortable: false,
                    width: 150,
                    showOnReadOnly: true
                },
                {
                    text: 'Rodzaj opakowania',
                    value: 'packageType',
                    align: 'center',
                    sortable: false,
                    width: 180,
                    showOnReadOnly: true
                },
                {
                    text: 'Ilość',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 80,
                    showOnReadOnly: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 120,
                    showOnReadOnly: false
                },
            ]
        }
    },
    computed: {
        calculationImpositionTasks() {
            if (this.isCalculationEditable) {
                return this.imposition.calculationImpositionTasks
            } else {
                return this.imposition.calculationImpositionTasks.filter(task => task.isSelected)
            }
        },
        calculationImpositionDieCutters() {
            return this.imposition.calculationImpositionDieCutters
        },
        calculationProducts() {
            return this.$store.state.calculations.calculations.calculation.calculationProducts
        },
        readOnlyFormatsHeaders() {
            return this.formatsHeaders.filter(item => item.showOnReadOnly)
        },
        readOnlyPackageHeaders() {
            return this.packagesHeaders.filter(item => item.showOnReadOnly)
        },
        plateDoesNotApply() {
            return this.imposition.plateTypeId === 7
        },
        isExpanded() {
            return this.currentTask.isExpanded
        }
    },
    watch: {
        impositionData: {
            handler: function(newValue) {
                this.imposition = newValue
                this.imposition.calculationImpositionTasks.map(task => { return { ...task, isExpanded: false }})
                this.openedPanel = null
                this.forceTaskComponentRerender()
            },
            deep: true
        },
    },
    methods: {
        isSlicingTask(taskId) {
            return taskId === appConfig.common.processes.slicingAfterPrintingTaskId || taskId === appConfig.common.processes.slicingAfterFinishingTaskId
        },
        setInputFormatsToSlicingTasks(imposition) {
            if (imposition.calculationImpositionTasks.length > 0) {
                const isPrintTaskSelected = imposition.calculationImpositionTasks.find(task => task.process.id === appConfig.common.processes.printTaskId).isSelected

                const inputFormatA = imposition.formatA
                const inputFormatB = imposition.formatB

                imposition.calculationImpositionTasks.forEach(task => {
                    if (this.isSlicingTask(task.processId)) {

                        this.$store.dispatch('calculations/getCalculationImpositionTask', task)
                            .then(response => {
                                response.data.calculationImpositionTaskCalculationProductComponents.forEach(productComponent => {
                                    isPrintTaskSelected ? productComponent.inputFormatA = inputFormatA : productComponent.inputFormatA = null
                                    isPrintTaskSelected ? productComponent.inputFormatB = inputFormatB : productComponent.inputFormatB = null
                                })
                                const taskToUpdate = {
                                    ...response.data,
                                    calculationImpositionId: this.imposition.id
                                }

                                this.$store.dispatch('calculations/updateCalculationImpositionTask', taskToUpdate)
                                    .then(response => {
                                        const taskIndex = this.imposition.calculationImpositionTasks.findIndex(taskItem => taskItem.id === response.data.id)

                                        this.imposition.calculationImpositionTasks[taskIndex].validated = response.data.validated
                                    })
                                    .catch(error => {
                                        throw error
                                    })
                            })
                            .catch(error => {
                                throw error
                            })
                    }
                })
            }
        },
        isTaskDisabled(task) {
            if (
                this.isCleaningAfterDieCuttingTaskId(task.processId) &&
                !this.isTaskSelected(appConfig.common.processes.dieCuttingTaskId)
            ) {
                return true
            }

            return !task.process.isDeletable || this.isCTPTask(task.processId)
        },
        isTaskSelected(taskId) {
            return this.imposition.calculationImpositionTasks.some(task => task.processId === taskId && task.isSelected)
        },
        isCleaningAfterDieCuttingTaskId(taskId) {
            return taskId === appConfig.common.processes.cleaningAfterDieCuttingTaskId
        },
        forceTaskComponentRerender() {
            this.taskComponentKey += 1
        },
        closePanel() {
            this.openedPanel = null
            this.forceTaskComponentRerender()
        },
        handleImpositionTask(task, taskIndex) {
            this.currentTaskIndex = taskIndex
            if (this.calculation.statusId === 15) { // zaakceptowana
                !this.currentTask.isExpanded ? this.getCalculationImpositionTask(task, taskIndex) : this.resetCalculationTask(task)

                return
            }

            if (this.currentTask.id && this.currentTask.id !== task.id) {
                this.resetCalculationTask(this.currentTask)
                if (this.$refs.panelContent && this.$refs.panelContent[0]) {
                    this.$refs.panelContent[0].handleTask(task, taskIndex)
                }

                return
            }
            if (this.isCalculationEditable) {
                if (!task.isExpanded) {
                    this.getCalculationImpositionTask(task, taskIndex)
                } else if (this.$refs.panelContent && this.$refs.panelContent[0]) {
                    this.$refs.panelContent[0].handleSelectedTask()
                }

                return

            }
        },
        updateTaskOnPanelClose(payload) {
            const { imposition, task, machineChanged } = payload
            const isImpositionChanged = imposition && !isEqual(this.initialImpositionObject, imposition)
            const isTaskChanged = !isEqual(this.initialTaskObject, task)

            if (isImpositionChanged) {
                this.updateCalculationImposition(imposition, task).then(() => {
                    this.resetCalculationTask(task)
                    if (isTaskChanged && machineChanged) {
                        this.getCalculationImposition(imposition)
                    }
                })
                this.$emit('update-calculation-paper-certificate', imposition)
            }
            if (isTaskChanged) {
                this.updateCalculationTask(task)
            }
            if (isEqual(this.initialTaskObject, task) && isEqual(this.initialImpositionObject, imposition)) {
                this.resetCalculationTask(task)
            }
        },
        updateTaskOnPanelChange(payload) {
            const { initialTaskObject, initialImpositionObject, task, imposition, taskToGet, index, machineChanged } = payload

            if (imposition && !isEqual(initialImpositionObject, imposition)) {
                this.updateCalculationImposition(imposition, task).then(() => {
                    this.resetCalculationTask(task)
                    this.getCalculationImpositionTask(taskToGet, index)
                    if (machineChanged) {
                        this.getCalculationImposition(imposition)
                    }
                })
                this.$emit('update-calculation-paper-certificate', imposition)
            }
            if (!isEqual(initialTaskObject, task)) {
                this.updateCalculationTask(task, machineChanged).then(() => {
                    this.getCalculationImpositionTask(taskToGet, index)
                })

                if (imposition && machineChanged) {
                    this.getCalculationImposition(imposition)
                }
            }
            if (isEqual(initialTaskObject, payload.task) || isEqual(initialImpositionObject, payload.imposition)) {
                this.getCalculationImpositionTask(taskToGet, index)
                this.resetCalculationTask(task)
            }
        },
        getCalculationImpositionTask(task, index) {
            this.$emit('calculation-loading', true)
            this.$store.dispatch('calculations/getCalculationImpositionTask', task)
                .then(response => {
                    const taskIndex = this.imposition.calculationImpositionTasks.findIndex(taskItem => {
                        return taskItem.id === response.data.id
                    })

                    this.imposition.calculationImpositionTasks[taskIndex] = JSON.parse(JSON.stringify(response.data))
                    this.imposition.calculationImpositionTasks[taskIndex].isExpanded = !task.isExpanded
                    this.imposition.calculationImpositionTasks[taskIndex].calculationImpositionId = this.imposition.id
                    this.currentTask = JSON.parse(JSON.stringify(this.imposition.calculationImpositionTasks[taskIndex]))
                    this.initialTaskObject = JSON.parse(JSON.stringify(this.imposition.calculationImpositionTasks[taskIndex]))
                    this.initialImpositionObject = this.imposition
                    this.openedPanel = index
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.$emit('calculation-loading', false)
                })

        },
        async getCalculationImposition(imposition) {
            this.$emit('calculation-loading', true)

            return await this.$store.dispatch('calculations/getCalculationImposition', imposition)
                .then(response => {
                    this.imposition.printMachine = JSON.parse(JSON.stringify(response.data.printMachine))
                    this.initialImpositionObject = this.imposition
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.$emit('calculation-loading', false)
                })
        },
        async updateCalculationImposition(imposition, task) {
            this.$emit('calculation-loading', true)

            let calculationImpositionDieCutters = imposition.calculationImpositionDieCutters.map(item => ({
                ...item,
                price: typeof item.price === 'string' ? +this.unformatPrice(item.price) : item.price
            }))

            return await this.$store.dispatch('calculations/updateCalculationImposition', { ...imposition, calculationImpositionDieCutters })
                .then(response => {
                    Object.entries(response.data).forEach(([key, value]) => {
                        this.imposition[key] = JSON.parse(JSON.stringify((value)))
                    })
                    this.imposition.paperCertificateId = this.imposition.paperCertificate?.id
                    this.initialImpositionObject = JSON.parse(JSON.stringify(this.imposition))

                    if (this.isPrintTask(task.processId)) {
                        this.setInputFormatsToSlicingTasks(imposition)
                    }

                    this.getCalculationValidation(imposition)
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.$emit('calculation-loading', false)
                })
        },
        async updateCalculationTask(task, machineChanged) {
            this.$emit('calculation-loading', true)

            return await this.$store.dispatch('calculations/updateCalculationImpositionTask', task)
                .then(response => {
                    task.validated = response.data.validated

                    if (machineChanged) {
                        task.isExpanded = !task.isExpanded
                    }
                    this.resetCalculationTask(task)
                    this.getCalculationValidation()
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.$emit('calculation-loading', false)
                })
        },
        resetCalculationTask(task) {
            const taskIndex = this.imposition.calculationImpositionTasks.findIndex(taskItem => {
                return taskItem.id === task.id
            })

            this.imposition.calculationImpositionTasks[taskIndex] = {
                id: task.id,
                process: {
                    id: task.process.id,
                    name: task.process.name,
                    isDeletable: task.process.isDeletable
                },
                processId: task.process.id,
                gangsOut: task.gangsOut,
                gangCreationAllowed: task.gangCreationAllowed,
                calculationImpositionTaskCalculationProductComponents: task.calculationImpositions,
                isSelected: task.isSelected,
                isShared: task.isShared,
                isExpanded: !task.isExpanded,
                validated: task.validated
            }
            this.initialTaskObject = {}
            this.currentTask = {}
            this.closePanel()
        },
        getCalculationValidation(imposition = null) {
            this.$store.dispatch('calculations/getCalculationValidation', this.calculation.id)
                .then(response => {
                    if (imposition) {

                        const impositionIndex = response.data.calculationImpositions.findIndex(impositionItem => {
                            return impositionItem.id === imposition.id
                        })

                        this.updateValidation(response.data.calculationImpositions[impositionIndex])
                    }

                    this.$emit('is-imposition-valid', response.data)
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.$emit('calculation-loading', false)
                })
        },
        updateValidation(validationData) {
            this.imposition.calculationImpositionTasks.forEach(task => {
                const taskIndex = validationData.calculationImpositionTasks.findIndex(taskItem => {
                    return taskItem.id === task.id
                })

                task.validated = validationData.calculationImpositionTasks[taskIndex].validated

            })
            this.forceTaskComponentRerender()
        },
        isCTPTask(taskId) {
            return taskId === appConfig.common.processes.ctpTaskId
        },
        isPrintTask(taskId) {
            return taskId === appConfig.common.processes.printTaskId
        },
        isCuttingPaperTask(taskId) {
            return taskId === appConfig.common.processes.cuttingPaperTaskId
        },
        isDieCuttingTask(taskId) {
            return taskId === appConfig.common.processes.dieCuttingTaskId
        },
        setSelectionForRelatedTasks(task) {
            if (this.isPrintTask(task.processId)) {
                this.imposition.calculationImpositionTasks = this.imposition.calculationImpositionTasks.map(item => this.isCTPTask(item.processId) ?
                    {...item, isSelected: task.isSelected} : item)
            }
            if (this.isDieCuttingTask(task.processId) && !this.isTaskSelected(task.processId)) {
                let cleaningAfterDieCuttingTask = this.imposition.calculationImpositionTasks.find(item => this.isCleaningAfterDieCuttingTaskId(item.processId))

                cleaningAfterDieCuttingTask && (cleaningAfterDieCuttingTask.isSelected = false)
            }
        },
        changeTaskSelection(task, isSelected) {
            addSentryBreadcrumb('ui.action', `${task.isSelected ? 'Wyłączenie' : 'Włączenie'} taska "${task.process.name}"`)

            this.$emit('calculation-loading', true)
            if (!task.isExpanded) {
                this.fetchTaskAndUpdateOnSelectChange(task, isSelected)
            } else {
                this.updateExpandedTaskOnSelectChange(task, isSelected)
            }
        },
        fetchTaskAndUpdateOnSelectChange(task, isSelected) {
            this.$store.dispatch('calculations/getCalculationImpositionTask', task)
                .then(response => {
                    const taskToUpdate = {
                        ...response.data,
                        calculationImpositionId: this.imposition.id,
                        isSelected: isSelected
                    }

                    this.$store.dispatch('calculations/updateCalculationImpositionTask', taskToUpdate)
                        .then(response => {
                            const taskIndex = this.imposition.calculationImpositionTasks.findIndex(taskItem => taskItem.id === response.data.id)

                            this.imposition.calculationImpositionTasks[taskIndex].validated = response.data.validated
                            this.imposition.calculationImpositionTasks[taskIndex].isSelected = response.data.isSelected
                            this.setSelectionForRelatedTasks(task)
                        })
                        .catch(error => {
                            throw error
                        })
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.$emit('calculation-loading', false)
                })
        },
        updateExpandedTaskOnSelectChange(task, isSelected) {
            const taskToUpdate = {
                ...task,
                calculationImpositionId: this.imposition.id,
                isSelected: isSelected
            }

            this.$store.dispatch('calculations/updateCalculationImpositionTask', taskToUpdate)
                .then(response => {
                    const taskIndex = this.imposition.calculationImpositionTasks.findIndex(taskItem => taskItem.id === response.data.id)

                    this.imposition.calculationImpositionTasks[taskIndex].validated = response.data.validated
                    this.imposition.calculationImpositionTasks[taskIndex] = {
                        ...response.data,
                        isExpanded: true,
                        calculationImpositionId: this.imposition.id
                    }
                    this.setSelectionForRelatedTasks(taskToUpdate)
                    this.initialTaskObject = JSON.parse(JSON.stringify(response.data))
                    this.currentTask = JSON.parse(JSON.stringify(this.imposition.calculationImpositionTasks[taskIndex]))
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.$emit('calculation-loading', false)
                })
        },
    }
}
</script>